import { Icon } from "@/components/icons";
import { entityTypeIcon } from "@/components/entity-type";
import { SearchMultiSelectWithTags } from "@/components/search-multi-select-with-tags";
import { FilterCard, FilterLabel } from "./primitives";
import { ENTITY_TYPES, type EntityType } from "./entity-types";

// "Media" isn't a pickable entity type — it's how adverse-media-sourced
// matches get tagged (Match.type), not something a user filters a search by.
// Kept in ENTITY_TYPES/EntityType (screening_service, monitoring_service rely
// on it) but excluded from the Entity Type picker's options.
const ENTITY_TYPE_ITEMS = ENTITY_TYPES.filter((t) => t !== "Media").map((t) => ({
  name: t,
  value: t,
  icon: <Icon name={entityTypeIcon(t)} size={20} />,
}));

export function EntityTypeCard({
  selected,
  onChange,
  className = "flex-1 justify-center w-full",
  defaultSelected,
}: {
  selected: EntityType[];
  onChange: (next: EntityType[]) => void;
  className?: string;
  /**
   * When provided, the "All" selector is dropped and emptying the selection
   * restores these values — the field can never be left with zero tags. Omit
   * it to keep the default behavior (an "All" chip stands in for an empty set).
   */
  defaultSelected?: EntityType[];
}) {
  const restoreOnEmpty = !!defaultSelected && defaultSelected.length > 0;
  return (
    <FilterCard className={className}>
      <FilterLabel>Entity type</FilterLabel>
      <SearchMultiSelectWithTags
        items={ENTITY_TYPE_ITEMS}
        selected={selected}
        onChange={(next) => {
          const typed = next as EntityType[];
          if (restoreOnEmpty && typed.length === 0) {
            onChange(defaultSelected);
            return;
          }
          // A real search only ever runs against one entity type — the
          // Individual+Organization pair shown by default is a "placebo" of
          // the backend's implicit behavior when nothing is picked, not a
          // real multi-select. Picking any type replaces whatever was
          // selected before (including that default pair) rather than
          // adding to it.
          if (restoreOnEmpty && typed.length > selected.length) {
            const added = typed.find((t) => !selected.includes(t));
            onChange(added ? [added] : typed);
            return;
          }
          onChange(typed);
        }}
        placeholder="Search entity types"
        allSelector={restoreOnEmpty ? undefined : { name: "All", preselected: true }}
      />
    </FilterCard>
  );
}
